import React, { useState, useEffect } from 'react';
import { Settings, Clock, Database, Trash2, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { useGlobalAPI } from '../../hooks/useGlobalAPI';
import { apiClient } from '../../services/apiClient';

const CacheSettings = ({ activeFile, onClose }) => {
  const api = useGlobalAPI(activeFile);
  const [settings, setSettings] = useState({
    ttl_minutes: 60,
    persist_indefinitely: false,
    cache_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/cache/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch cache settings:', error);
      setMessage({ type: 'error', text: 'Failed to load cache settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/cache/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch cache stats:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put('/cache/settings', settings);
      setSettings(response.data);
      setMessage({ type: 'success', text: response.data.message });
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to save cache settings:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save cache settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleClearPersistentCache = async () => {
    try {
      setClearing(true);
      const response = await apiClient.delete('/cache/persistent');
      setMessage({ type: 'success', text: response.data.message });
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to clear persistent cache:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to clear persistent cache' });
    } finally {
      setClearing(false);
    }
  };

  const formatTTLOptions = () => {
    return [
      { value: 5, label: '5 minutes' },
      { value: 15, label: '15 minutes' },
      { value: 30, label: '30 minutes' },
      { value: 60, label: '1 hour' },
      { value: 120, label: '2 hours' },
      { value: 240, label: '4 hours' },
      { value: 480, label: '8 hours' },
      { value: 1440, label: '1 day' },
      { value: 2880, label: '2 days' },
      { value: 10080, label: '1 week' }
    ];
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-96">
          <div className="flex items-center justify-center">
            <Loader className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading cache settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold">Cache Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {message && (
            <div className={`flex items-center space-x-2 p-3 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Cache Statistics */}
          {stats && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Database className="h-4 w-4 mr-2" />
                Cache Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Hit Rate</p>
                  <p className="font-semibold text-lg text-green-600">{stats.hit_rate}%</p>
                </div>
                <div>
                  <p className="text-gray-600">Cache Size</p>
                  <p className="font-semibold text-lg">{stats.cache_size} entries</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Hits</p>
                  <p className="font-semibold text-lg text-blue-600">{stats.hit_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Misses</p>
                  <p className="font-semibold text-lg text-orange-600">{stats.miss_count}</p>
                </div>
              </div>
            </div>
          )}

          {/* Cache Enable/Disable Toggle */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="cache_enabled"
                checked={settings.cache_enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, cache_enabled: e.target.checked }))}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <label htmlFor="cache_enabled" className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                  <Database className="h-4 w-4 mr-2" />
                  Enable caching
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  When disabled, cache reads return empty and writes are skipped. Use during development to always fetch fresh data.
                </p>
              </div>
            </div>
            {!settings.cache_enabled && (
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-yellow-50 text-yellow-800">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">Caching is disabled — all requests fetch fresh data. Performance will be reduced.</span>
              </div>
            )}
          </div>

          {/* TTL Setting */}
          <div className="space-y-3">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <Clock className="h-4 w-4 mr-2" />
              Default Cache TTL (Time To Live)
            </label>
            <select
              value={settings.ttl_minutes}
              onChange={(e) => setSettings(prev => ({ ...prev, ttl_minutes: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={settings.persist_indefinitely}
            >
              {formatTTLOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {settings.persist_indefinitely && (
              <p className="text-sm text-gray-500">TTL setting is disabled when persistence is enabled</p>
            )}
          </div>

          {/* Persistence Setting */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="persist"
                checked={settings.persist_indefinitely}
                onChange={(e) => setSettings(prev => ({ ...prev, persist_indefinitely: e.target.checked }))}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <label htmlFor="persist" className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                  <Database className="h-4 w-4 mr-2" />
                  Persist cache indefinitely
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  When enabled, cache entries are stored on disk and persist across application restarts 
                  until files are reloaded. TTL settings are ignored in this mode.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
            
            {settings.persist_indefinitely && (
              <button
                onClick={handleClearPersistentCache}
                disabled={clearing}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {clearing ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>{clearing ? 'Clearing...' : 'Clear Persistent Files'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CacheSettings;